/**
 * FigureExtension — Custom TipTap node for images with caption + credit.
 *
 * Renders as:
 *   <figure>
 *     <img src="..." />
 *     <figcaption>Caption text</figcaption>
 *     <small class="credit">Photo: Credit text</small>
 *   </figure>
 *
 * Attributes: src (required), caption (optional), credit (optional)
 *
 * This extension replaces the default Image node when the blog type
 * supports images. It stores caption and credit as node attributes,
 * making them part of the TipTap JSON document.
 */

import { Node, mergeAttributes } from '@tiptap/react';

export interface FigureOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    figure: {
      setFigure: (options: { src: string; caption?: string; credit?: string }) => ReturnType;
    };
  }
}

export const Figure = Node.create<FigureOptions>({
  name: 'figure',
  group: 'block',
  content: '',
  draggable: true,
  isolating: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      src: { default: null },
      caption: { default: '' },
      credit: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'figure' }];
  },

  renderHTML({ HTMLAttributes }) {
    const { src, caption, credit, ...rest } = HTMLAttributes;
    const children: any[] = [
      ['img', mergeAttributes(rest, { src, draggable: false })],
    ];
    if (caption) {
      children.push(['figcaption', {}, caption]);
    }
    if (credit) {
      children.push(['small', { class: 'figure-credit' }, credit]);
    }
    return ['figure', mergeAttributes(this.options.HTMLAttributes), ...children];
  },

  addCommands() {
    return {
      setFigure:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      // Track current attrs so input handlers always read fresh values
      let currentAttrs = { ...node.attrs };

      const dom = document.createElement('figure');
      dom.style.margin = '16px 0';
      dom.style.position = 'relative';

      // Image
      const img = document.createElement('img');
      img.src = currentAttrs.src;
      img.style.width = '100%';
      img.style.borderRadius = '8px';
      img.style.display = 'block';
      img.draggable = false;
      dom.appendChild(img);

      // Helper: update a single attribute without touching the others
      function setAttr(key: string, value: string) {
        if (typeof getPos !== 'function') return;
        const pos = getPos();
        if (pos == null) return;
        // Read current attrs from the resolved node in the doc
        const resolvedNode = editor.view.state.doc.nodeAt(pos);
        if (!resolvedNode) return;
        editor.view.dispatch(
          editor.view.state.tr.setNodeMarkup(pos, undefined, {
            ...resolvedNode.attrs,
            [key]: value,
          })
        );
      }

      // Caption input
      const captionInput = document.createElement('input');
      captionInput.type = 'text';
      captionInput.placeholder = 'Add a caption…';
      captionInput.value = currentAttrs.caption || '';
      captionInput.style.cssText =
        'width:100%;border:none;outline:none;background:transparent;font-size:13px;font-style:italic;' +
        'color:var(--text-secondary);padding:6px 0 2px;font-family:inherit;box-sizing:border-box;';
      // Debounce: only persist on blur to avoid mid-keystroke transactions
      captionInput.addEventListener('blur', () => {
        if (captionInput.value !== currentAttrs.caption) {
          setAttr('caption', captionInput.value);
        }
      });
      dom.appendChild(captionInput);

      // Credit input
      const creditInput = document.createElement('input');
      creditInput.type = 'text';
      creditInput.placeholder = 'Photo credit…';
      creditInput.value = currentAttrs.credit || '';
      creditInput.style.cssText =
        'width:100%;border:none;outline:none;background:transparent;font-size:11px;' +
        'color:var(--text-muted);padding:0 0 4px;font-family:inherit;box-sizing:border-box;';
      creditInput.addEventListener('blur', () => {
        if (creditInput.value !== currentAttrs.credit) {
          setAttr('credit', creditInput.value);
        }
      });
      dom.appendChild(creditInput);

      return {
        dom,

        // CRITICAL: Tell ProseMirror to NOT handle events that target
        // our caption/credit inputs. Without this, ProseMirror intercepts
        // keystrokes (Backspace, Enter, etc.) and corrupts the node.
        stopEvent: (event: Event) => {
          const target = event.target;
          if (target === captionInput || target === creditInput) return true;
          return false;
        },

        // Tell ProseMirror to ignore DOM mutations inside this node view
        // (we manage our own DOM updates in the `update` method).
        ignoreMutation: () => true,

        update: (updatedNode) => {
          if (updatedNode.type.name !== 'figure') return false;
          currentAttrs = { ...updatedNode.attrs };
          img.src = currentAttrs.src;
          // Only update input values if they differ (preserve cursor position)
          if (document.activeElement !== captionInput && captionInput.value !== currentAttrs.caption) {
            captionInput.value = currentAttrs.caption || '';
          }
          if (document.activeElement !== creditInput && creditInput.value !== currentAttrs.credit) {
            creditInput.value = currentAttrs.credit || '';
          }
          return true;
        },
      };
    };
  },
});
